import React, { useState } from 'react';
import { useAuth } from 'context/auth';
import styles from './styles.module.css';
import Image from 'next/image';
import { Icon } from 'common/icon';
import clsx from 'clsx';

import { IComment, IComplaint, IQuestion, IReply } from 'constants/types';
import { ShareModal } from 'components/modal/share';

import { useLoginModal } from 'hooks';

import { Button } from 'common/new-button';
import { LoginModal } from 'components/modal/login';
import { ReplyComplaint, ReplyPublication } from './replies';
import { formatCreatedAtDate } from 'utils/formatDays';
import { BlurHashImage } from 'components/blurhash-image';
import Link from 'next/link';

interface AvatarProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
}

export const Avatar = ({
  src,
  alt,
  width = 50,
  height = 50,
  className,
}: AvatarProps) => {
  const hasSrc = Boolean(src?.trim());

  return (
    <div
      className={styles.avatar_container}
      style={{ width, height, minWidth: width, minHeight: height }}
    >
      {hasSrc ? (
        <Image
          width={width}
          height={height}
          src={src}
          alt={alt}
          className={clsx(styles.avatar, className)}
        />
      ) : (
        <span
          className={clsx(styles.avatar, styles.avatar_fallback, className)}
          aria-hidden
        >
          <Icon.User
            width={Math.min(width, 24).toString()}
            height={Math.min(height, 24).toString()}
          />
        </span>
      )}
    </div>
  );
};

interface ActionItemProps {
  icon: React.JSX.Element;
  text: string;
  action?: (reply: IComplaint) => void;
  item?: IComplaint | undefined;
  isActive?: boolean;
}

export const ActionItem = ({
  icon,
  text,
  action,
  item,
  isActive,
}: ActionItemProps) => {
  const handleClick = () => {
    if (action && item) {
      action(item);
    }
  };
  return (
    <div
      className={clsx(styles.actionItem, { [styles.clicked]: isActive })}
      onClick={handleClick}
    >
      {icon}
      <div className={styles.actionText}>{text}</div>
    </div>
  );
};

interface CommentProps {
  data: IComplaint | IComment | IQuestion;
  reply?: IReply;
  onReplyClick?: (reply: IComplaint) => void;
  isActive?: boolean;
  likeCount: number;
  useReply?: boolean;
}

export const CommentPublication = ({
  data,
  likeCount,
  isActive,
  onReplyClick,
  useReply = true,
}: CommentProps) => {
  const [isModalVisible, setModalVisible] = useState<boolean>(false);
  const {
    handleCloseLoginModal,
    handleConfirmLoginModal,
    handleVisibleLoginModal,
    isLoginVisible,
  } = useLoginModal();
  const { isLoggedIn } = useAuth();
  return (
    <article className={styles.container}>
      <header className={styles.header}>
        <div className={styles.user}>
          <div className={styles.avatar_wrapper}>
            {data.user.imageUrl && (
              <Avatar
                src={data.user.imageUrl}
                alt={data.user.imageUrl}
                className={styles.avatar_comment}
              />
            )}
            <div className={styles.info}>
              <h2 className={styles.name}>{data.user.username}</h2>
              <time className={styles.date}>
                {formatCreatedAtDate(data.createdAt)}
              </time>
            </div>
          </div>
          <div className={clsx(styles.content, styles.content_margin)}>
            <p className={styles.text}>{data.message}</p>
            {data.imageUrl && (
              <Link href={data.imageUrl} className={styles.attachedImage}>
                <BlurHashImage
                  src={data.imageUrl ?? ''}
                  alt="Attached image"
                  width={100}
                  height={100}
                  blurhash={data.blurHash}
                />
              </Link>
            )}
            <div className={styles.actionBar}>
              {useReply && (
                <ActionItem
                  icon={<Icon.Reply />}
                  text="Reply"
                  isActive={isActive}
                  item={data as IComplaint}
                  action={onReplyClick}
                />
              )}
              <div className={styles.likeCount}>
                <ActionItem icon={<Icon.LikeComment />} text={`${likeCount}`} />
              </div>
              <div className={styles.shareAction}>
                <Button
                  icon={<Icon.Share />}
                  className={styles.button}
                  text={'Share'}
                  onClick={() =>
                    isLoggedIn
                      ? setModalVisible(true)
                      : handleVisibleLoginModal()
                  }
                />
              </div>
            </div>
            {data.reply && <ReplyPublication reply={data.reply} />}
          </div>
          <ShareModal isVisible={isModalVisible} setVisible={setModalVisible} />
          <LoginModal
            isVisible={isLoginVisible}
            onClose={handleCloseLoginModal}
            confirmOnClick={handleConfirmLoginModal}
          />
        </div>
      </header>
    </article>
  );
};

export const CommentComplaint = ({
  data,
  onReplyClick,
  isActive,
  likeCount,
}: CommentProps) => {
  const [isModalVisible, setModalVisible] = useState<boolean>(false);
  const {
    handleCloseLoginModal,
    handleConfirmLoginModal,
    handleVisibleLoginModal,
    isLoginVisible,
  } = useLoginModal();
  const { isLoggedIn } = useAuth();

  return (
    <article className={clsx(styles.container, styles.container_big_width)}>
      <header className={styles.header}>
        <div className={styles.avatar_wrapper_complaint}>
          {data.user.imageUrl && (
            <Avatar
              src={data.user.imageUrl}
              alt={data.user.imageUrl}
              className={styles.avatar_comment}
            />
          )}
          <h2 className={clsx(styles.name, styles.name_huge)}>
            {data.user.username}
          </h2>
        </div>
        <div className={clsx(styles.content, styles.content_huge)}>
          {data.reply && (
            <div className={styles.reply_complaint__container}>
              <ReplyComplaint
                reply={
                  data.reply?.reply ? (data.reply as any)?.reply_id : data.reply
                }
                username={data.reply.user_id.username}
              />
            </div>
          )}
          <time className={clsx(styles.date, styles.no_margin)}>
            {formatCreatedAtDate(data.createdAt)}
          </time>
          <p className={styles.text}>{data.message}</p>
          {data.imageUrl && (
            <Link
              target="_blank"
              href={data.imageUrl}
              className={styles.attachedImage}
            >
              <BlurHashImage
                src={data.imageUrl ?? ''}
                alt="Attached image"
                width={100}
                height={100}
                blurhash={data.blurHash}
              />
            </Link>
          )}
          <div className={clsx(styles.actionBar, styles.actionBar_huge)}>
            <ActionItem
              icon={<Icon.Reply />}
              text="Reply"
              action={onReplyClick}
              item={data as IComplaint}
              isActive={isActive}
            />
            <div className={styles.shareAction}>
              <Button
                icon={<Icon.Share />}
                className={styles.button}
                text={'Share'}
                onClick={() =>
                  isLoggedIn ? setModalVisible(true) : handleVisibleLoginModal()
                }
              />
            </div>
          </div>
        </div>
        <ShareModal isVisible={isModalVisible} setVisible={setModalVisible} />
        <LoginModal
          isVisible={isLoginVisible}
          onClose={handleCloseLoginModal}
          confirmOnClick={handleConfirmLoginModal}
        />
      </header>
    </article>
  );
};
